import React, { useEffect, useMemo, useState } from "react";
import { LayoutGrid, Map as MapIcon, LocateFixed, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageTitle } from "@/components/SettingsPanel";
import { SpotCard } from "@/components/SpotCard";
import { SuggestSpot } from "@/components/SuggestSpot";
import { ExploreMap, MapPoint } from "@/components/ExploreMap";
import { GOVERNORATES } from "@/pages/PartnerRegister";
import { api } from "@/lib/api";
import type { Spot } from "@/lib/spots";
import { useSpotCategories } from "@/hooks/useSpotCategories";
import { usePrefs } from "@/contexts/PrefsContext";

const pill = (on: boolean) => `h-10 px-4 shrink-0 rounded-full text-sm font-bold border transition-[background-color,color] ${on ? "bg-foreground text-background border-foreground" : "bg-card hover:bg-accent"}`;

export default function CustomerSpots() {
  const { t, lang } = usePrefs();
  const [cat, setCat] = useState("");
  const cats = useSpotCategories();
  const [gov, setGov] = useState("");
  const [view, setView] = useState<"list" | "map">("list");
  const [spots, setSpots] = useState<Spot[] | null>(null);
  const [near, setNear] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const locate = () => {
    if (near) return setNear(null);
    if (!navigator.geolocation) return toast.error(t("spot.noGeo"));
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setNear({ lat: +p.coords.latitude.toFixed(5), lng: +p.coords.longitude.toFixed(5) }); setLocating(false); },
      () => { toast.error(t("spot.geoDenied")); setLocating(false); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };

  useEffect(() => {
    setSpots(null);
    api.get("/public-spots", { params: { category: cat || undefined, governorate: gov || undefined, lat: near?.lat, lng: near?.lng } }).then((r) => setSpots(r.data)).catch(() => setSpots([]));
  }, [cat, gov, near]);

  const points: MapPoint[] = useMemo(() => [
    ...(spots || []).map((s) => ({ id: s.spot_id, kind: (s.kind === "venue" ? "venue" : "spot") as "venue" | "spot", lat: s.lat, lng: s.lng, name: lang === "en" && s.name_en ? s.name_en : s.name_ar, to: `/spots/${s.spot_id}` })),
  ], [spots, lang]);

  const toggle = (k: "list" | "map", Icon: typeof MapIcon) => (
    <button data-testid={`spots-view-${k}`} aria-pressed={view === k} onClick={() => setView(k)} className={`h-10 w-10 grid place-items-center rounded-full ${view === k ? "bg-primary text-primary-foreground" : "bg-card border"}`}><Icon className="h-4 w-4" /></button>
  );

  return (
    <div data-testid="customer-spots-page" className="space-y-5">
      <PageTitle title={t("spot.title")} sub={t("spot.sub")} action={<div className="flex gap-2">{toggle("list", LayoutGrid)}{toggle("map", MapIcon)}</div>} />
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="spot-cat-pills">
        <button className={pill(!cat)} onClick={() => setCat("")} data-testid="spot-cat-all">{t("type.all")}</button>
        {cats.map((c) => <button key={c.slug} className={pill(cat === c.slug)} onClick={() => setCat(c.slug)} data-testid={`spot-cat-${c.slug}`}>{lang === "ar" ? c.name_ar : c.name_en}</button>)}
      </div>
      <div className="flex flex-wrap gap-2">
      <button data-testid="spot-near-me" aria-pressed={!!near} onClick={locate} disabled={locating} className={`${pill(!!near)} inline-flex items-center gap-1.5`}>
        {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}{t("spot.nearMe")}
      </button>
      <select value={gov} onChange={(e) => setGov(e.target.value)} data-testid="spot-filter-governorate" className="h-10 rounded-full border bg-card px-3 text-sm font-semibold">
        <option value="">{t("ev.allGov")}</option>
        {GOVERNORATES.map((g) => <option key={g} value={g}>{t(`gov.${g}`)}</option>)}
      </select>
      </div>
      {view === "map" ? (
        <div className="space-y-2">
          <ExploreMap points={points} />
          <div className="flex gap-4 text-xs font-bold text-muted-foreground" data-testid="map-legend">
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-emerald-600" />{t("spot.legendSpot")}</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-rose-600" />{t("spot.legendVenue")}</span>
          </div>
        </div>
      ) : spots === null ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] rounded-[1.75rem] bg-muted animate-pulse" />)}</div>
      ) : spots.length === 0 ? (
        <p data-testid="spots-empty" className="text-center text-muted-foreground py-16">{t("spot.empty")}</p>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{spots.map((s, i) => <SpotCard key={s.spot_id} s={s} index={i} />)}</div>
      )}
      <SuggestSpot />
    </div>
  );
}
